import type { FC, ReactNode } from 'react'
import type { HomeContent, HomePageContent } from '@/types'

import { ArrowUp, ChevronDown, Quote } from 'lucide-react'
import SectionHeading from '@/components/landing/section-heading'

interface PageProps {
    page: HomePageContent
}

export const IntroSection: FC<PageProps> = ({ page }): ReactNode => {
    if (!page.introHeading && !page.introBody) return null

    return (
        <section id='intro' aria-labelledby='intro-title' className='scroll-mt-8 px-6 py-24 sm:py-32'>
            <div className='mx-auto grid max-w-5xl gap-8 md:grid-cols-[2fr_3fr] md:gap-16'>
                {page.introHeading && (
                    <h2 id='intro-title' className='font-heading text-4xl leading-tight tracking-tight text-balance text-foreground sm:text-5xl'>
                        {page.introHeading}
                    </h2>
                )}

                {page.introBody && (
                    <p className='text-xl leading-relaxed text-pretty text-muted-foreground first-letter:float-left first-letter:mr-3 first-letter:font-heading first-letter:text-6xl first-letter:leading-none first-letter:text-primary'>
                        {page.introBody}
                    </p>
                )}
            </div>
        </section>
    )
}

export const HighlightsSection: FC<PageProps & { items: HomeContent['highlights'] }> = ({ page, items }): ReactNode => {
    if (items.length === 0) return null

    return (
        <section id='highlights' aria-labelledby='highlights-title' className='scroll-mt-8 bg-secondary/60 px-6 py-24 sm:py-32'>
            <div className='mx-auto max-w-6xl'>
                <SectionHeading id='highlights-title' title={page.highlightsHeading} body={page.highlightsBody} />

                <ul className='mt-14 grid gap-6 md:grid-cols-3'>
                    {items.map((item, index) => (
                        <li key={item.id} className='flex flex-col rounded-2xl border bg-card p-8 shadow-sm transition-shadow hover:shadow-md'>
                            <span aria-hidden='true' className='font-heading text-sm font-semibold tracking-widest text-primary'>
                                {String(index + 1).padStart(2, '0')}
                            </span>

                            <h3 className='mt-6 font-heading text-2xl leading-snug text-card-foreground'>
                                {item.title}
                            </h3>

                            {item.body && (
                                <p className='mt-3 leading-relaxed text-muted-foreground'>
                                    {item.body}
                                </p>
                            )}
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    )
}

export const FindsSection: FC<PageProps & { items: HomeContent['finds'] }> = ({ page, items }): ReactNode => {
    if (items.length === 0) return null

    return (
        <section id='finds' aria-labelledby='finds-title' className='scroll-mt-8 px-6 py-24 sm:py-32'>
            <div className='mx-auto grid max-w-5xl gap-12 md:grid-cols-2 md:gap-16'>
                <SectionHeading id='finds-title' title={page.findsHeading} body={page.findsBody} className='md:mx-0 md:text-left' />

                <ol className='divide-y border-y'>
                    {items.map((item, index) => (
                        <li key={item.id} className='flex gap-5 py-6'>
                            <span aria-hidden='true' className='flex size-9 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-semibold text-accent-foreground'>
                                {index + 1}
                            </span>

                            <div>
                                <h3 className='text-lg font-semibold text-foreground'>
                                    {item.name}
                                </h3>

                                {item.detail && (
                                    <p className='mt-1 leading-relaxed text-muted-foreground'>
                                        {item.detail}
                                    </p>
                                )}
                            </div>
                        </li>
                    ))}
                </ol>
            </div>
        </section>
    )
}

export const QuoteSection: FC<PageProps> = ({ page }): ReactNode => {
    if (!page.quoteText) return null

    return (
        <section id='quote' aria-label='Quote' className='px-6 pb-24 sm:pb-32'>
            <figure className='mx-auto max-w-4xl text-center'>
                <Quote aria-hidden='true' className='mx-auto size-6 text-primary' />

                <blockquote className='mt-6 font-heading text-3xl leading-snug text-balance text-foreground italic sm:text-4xl'>
                    <p>{page.quoteText}</p>
                </blockquote>

                {(page.quoteAuthor || page.quoteSource) && (
                    <figcaption className='mt-8 text-sm'>
                        {page.quoteAuthor && <cite className='block font-semibold text-foreground not-italic'>{page.quoteAuthor}</cite>}
                        {page.quoteSource && <span className='mt-1 block text-muted-foreground'>{page.quoteSource}</span>}
                    </figcaption>
                )}
            </figure>
        </section>
    )
}

export const FactsSection: FC<PageProps & { items: HomeContent['facts'] }> = ({ page, items }): ReactNode => {
    if (items.length === 0) return null

    return (
        <section id='facts' aria-labelledby='facts-title' className='scroll-mt-8 bg-primary px-6 py-24 sm:py-28'>
            <div className='mx-auto max-w-6xl'>
                <SectionHeading id='facts-title' title={page.factsHeading} inverted />

                <dl className='mt-14 grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-4'>
                    {items.map(item => (
                        <div key={item.id} className='border-t border-primary-foreground/20 pt-6'>
                            <dt className='sr-only'>{item.label ?? item.value}</dt>
                            <dd className='font-heading text-5xl tracking-tight text-primary-foreground'>
                                {item.value}
                            </dd>
                            {item.label && (
                                <dd className='mt-3 leading-relaxed text-primary-foreground/80'>
                                    {item.label}
                                </dd>
                            )}
                        </div>
                    ))}
                </dl>
            </div>
        </section>
    )
}

export const FaqSection: FC<PageProps & { items: HomeContent['faqs'] }> = ({ page, items }): ReactNode => {
    if (items.length === 0) return null

    return (
        <section id='faq' aria-labelledby='faq-title' className='scroll-mt-8 px-6 py-24 sm:py-32'>
            <div className='mx-auto max-w-3xl'>
                <SectionHeading id='faq-title' title={page.faqHeading} body={page.faqBody} />

                <div className='mt-12 divide-y rounded-2xl border bg-card'>
                    {items.map(item => (
                        <details key={item.id} className='group px-6 [&_summary::-webkit-details-marker]:hidden'>
                            <summary className='flex cursor-pointer list-none items-center justify-between gap-6 py-5 text-left text-lg font-semibold text-card-foreground focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring/40 rounded-md'>
                                <span>{item.question}</span>
                                <ChevronDown aria-hidden='true' className='size-5 shrink-0 text-primary transition-transform duration-200 group-open:rotate-180' />
                            </summary>

                            {item.answer && (
                                <p className='pb-6 leading-relaxed text-muted-foreground'>
                                    {item.answer}
                                </p>
                            )}
                        </details>
                    ))}
                </div>
            </div>
        </section>
    )
}

export const CtaSection: FC<PageProps> = ({ page }): ReactNode => {
    if (!page.ctaHeading && !page.ctaBody) return null

    return (
        <section id='cta' aria-labelledby='cta-title' className='px-6 pb-24 sm:pb-32'>
            <div className='mx-auto max-w-5xl rounded-3xl bg-accent px-8 py-16 text-center sm:px-16'>
                <SectionHeading id='cta-title' title={page.ctaHeading} body={page.ctaBody} />

                {page.ctaButtonLabel && (
                    <a
                        href='#hero'
                        className='mt-8 inline-flex h-12 items-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus-visible:ring-4 focus-visible:ring-ring/40 focus-visible:outline-none'
                    >
                        <span>{page.ctaButtonLabel}</span>
                        <ArrowUp aria-hidden='true' className='size-4' />
                    </a>
                )}
            </div>
        </section>
    )
}
